'use client';

import { useEffect, useRef, useState, useTransition } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { ChevronDown } from 'lucide-react';

const languageOptions = [
  { code: 'es', label: 'ES', name: 'Español', flag: 'co' },
  { code: 'en', label: 'EN', name: 'English', flag: 'gb' },
  { code: 'pt', label: 'PT', name: 'Português', flag: 'br' },
  { code: 'fr', label: 'FR', name: 'Français', flag: 'fr' },
  { code: 'de', label: 'DE', name: 'Deutsch', flag: 'de' },
  { code: 'nl', label: 'NL', name: 'Nederlands', flag: 'nl' },
  { code: 'it', label: 'IT', name: 'Italiano', flag: 'it' },
  { code: 'ru', label: 'RU', name: 'Русский', flag: 'ru' },
  { code: 'zh', label: 'ZH', name: '中文', flag: 'cn' },
  { code: 'ja', label: 'JA', name: '日本語', flag: 'jp' },
  { code: 'ko', label: 'KO', name: '한국어', flag: 'kr' },
  { code: 'ar', label: 'AR', name: 'العربية', flag: 'sa' },
  { code: 'hi', label: 'HI', name: 'हिंदी', flag: 'in' },
  { code: 'tr', label: 'TR', name: 'Türkçe', flag: 'tr' },
  { code: 'pl', label: 'PL', name: 'Polski', flag: 'pl' },
];

function Flag({ countryCode, className = '' }) {
  return (
    <img
      src={`https://flagcdn.com/w40/${countryCode}.png`}
      srcSet={`https://flagcdn.com/w80/${countryCode}.png 2x`}
      width={28}
      height={21}
      alt=""
      loading="lazy"
      decoding="async"
      className={`inline-block rounded-[3px] object-cover shadow-sm ${className}`}
    />
  );
}

export default function LanguageSelector() {
  const router = useRouter();
  const pathname = usePathname();
  const [isPending, startTransition] = useTransition();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const currentLocale = pathname ? pathname.split('/')[1] : 'es';
  const current =
    languageOptions.find((option) => option.code === currentLocale) ||
    languageOptions[0];

  useEffect(() => {
    if (!open) return undefined;

    function onPointerDown(event) {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    }

    function onKeyDown(event) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  function changeLocale(nextLocale) {
    if (!pathname || nextLocale === currentLocale) {
      setOpen(false);
      return;
    }

    const segments = pathname.split('/');
    segments[1] = nextLocale;
    const nextPathname = segments.join('/');

    setOpen(false);
    startTransition(() => {
      router.push(nextPathname);
      router.refresh();
    });
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        disabled={isPending}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={currentLocale === 'es' ? 'Seleccionar idioma' : 'Select language'} // i18n-guard-ignore
        onClick={() => setOpen((value) => !value)}
        className="inline-flex items-center gap-2 rounded-xl border border-cyan-500/50 bg-[#0F172A] px-2.5 py-1.5 text-sm font-bold text-cyan-100 shadow-[0_0_10px_rgba(6,182,212,0.2)] transition-all hover:border-cyan-400 disabled:opacity-50"
      >
        <Flag countryCode={current.flag} className="h-[18px] w-6" />
        <span className="min-w-[1.5rem] text-left">{current.label}</span>
        <ChevronDown
          size={14}
          className={`text-cyan-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <ul
          role="listbox"
          aria-label={currentLocale === 'es' ? 'Idiomas' : 'Languages'} // i18n-guard-ignore
          className="absolute right-0 top-full z-[120] mt-2 max-h-80 w-56 overflow-y-auto rounded-2xl border border-cyan-500/30 bg-[#0B1220] py-2 shadow-2xl"
        >
          {languageOptions.map((option) => {
            const selected = option.code === current.code;
            return (
              <li key={option.code} role="option" aria-selected={selected}>
                <button
                  type="button"
                  onClick={() => changeLocale(option.code)}
                  className={`flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                    selected
                      ? 'bg-cyan-500/20 text-cyan-100'
                      : 'text-slate-200 hover:bg-cyan-500/10 hover:text-white'
                  }`}
                >
                  <Flag countryCode={option.flag} className="h-5 w-7 shrink-0" />
                  <span className="text-sm font-black tracking-wide">{option.label}</span>
                  <span className="truncate text-xs font-medium text-slate-400">
                    {option.name}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
